export class POSActivitySessionBridgeError extends Error {
  constructor(
    message,
    {
      code =
        "POS_ACTIVITY_SESSION_BRIDGE_ERROR",
      operation = null,
      stage = null,
      details = {},
      cause = null,
    } = {}
  ) {
    super(message);

    this.name =
      "POSActivitySessionBridgeError";

    this.code = code;
    this.operation = operation;
    this.stage = stage;

    this.details = Object.freeze({
      ...details,
    });

    if (cause !== null) {
      this.cause = cause;
    }
  }
}


/**
 * Generic bridge between POSActivity and the existing
 * CashSession runtime.
 *
 * The bridge owns no business state and performs no
 * cash-session calculation.
 *
 * Responsibilities:
 *
 * - preserve company/activity scope;
 * - delegate opening to the injected runtime;
 * - delegate current-session resolution;
 * - delegate closing;
 * - delegate history;
 * - delegate session reporting;
 * - normalize sync/async runtimes;
 * - preserve underlying failures as causes.
 *
 * Explicitly excluded:
 *
 * - CashSession persistence;
 * - session calculations;
 * - expected cash calculation;
 * - cash difference calculation;
 * - DailyClosure creation;
 * - Payment;
 * - Receipt;
 * - Inventory;
 * - Accounting;
 * - NF525;
 * - React state;
 * - global state.
 */
export function createPOSActivitySessionBridge({
  sessionRuntime,
} = {}) {
  assertSessionRuntime(
    sessionRuntime
  );


  async function openSession(
    context = {},
    payload = {}
  ) {
    const scope =
      normalizeSessionScope(
        context,
        "openSession"
      );

    const normalizedPayload =
      normalizePayload(
        payload,
        "openSession"
      );

    return invokeRuntime(
      "openSession",
      () =>
        sessionRuntime.openSession(
          {
            ...normalizedPayload,
            activity_uuid:
              scope.activityUuid,
          },
          {
            companyUuid:
              scope.companyUuid,
          }
        ),
      {
        scope,
      }
    );
  }


  async function resolveCurrentSession(
    context = {}
  ) {
    const scope =
      normalizeSessionScope(
        context,
        "resolveCurrentSession"
      );

    return invokeRuntime(
      "resolveCurrentSession",
      () =>
        sessionRuntime
          .resolveCurrentSession({
            companyUuid:
              scope.companyUuid,

            activityUuid:
              scope.activityUuid,
          }),
      {
        scope,
      }
    );
  }


  async function closeSession(
    context = {},
    cashSessionUuid,
    payload = {}
  ) {
    const scope =
      normalizeSessionScope(
        context,
        "closeSession"
      );

    const normalizedSessionUuid =
      normalizeRequiredString(
        cashSessionUuid,
        "cashSessionUuid",
        {
          operation:
            "closeSession",
          code:
            "INVALID_CASH_SESSION_UUID",
        }
      );

    const normalizedPayload =
      normalizePayload(
        payload,
        "closeSession"
      );

    return invokeRuntime(
      "closeSession",
      () =>
        sessionRuntime.closeSession(
          normalizedSessionUuid,
          normalizedPayload,
          {
            companyUuid:
              scope.companyUuid,
            activityUuid:
              scope.activityUuid,
          }
        ),
      {
        scope,
        cashSessionUuid:
          normalizedSessionUuid,
      }
    );
  }


  async function getSessionHistory(
    context = {}
  ) {
    const scope =
      normalizeSessionScope(
        context,
        "getSessionHistory"
      );

    return invokeRuntime(
      "getSessionHistory",
      () =>
        sessionRuntime
          .getSessionHistory({
            companyUuid:
              scope.companyUuid,
            activityUuid:
              scope.activityUuid,
          }),
      {
        scope,
      }
    );
  }


  async function getSessionReport(
    context = {},
    cashSessionUuid
  ) {
    const scope =
      normalizeSessionScope(
        context,
        "getSessionReport"
      );

    const normalizedSessionUuid =
      normalizeRequiredString(
        cashSessionUuid,
        "cashSessionUuid",
        {
          operation:
            "getSessionReport",
          code:
            "INVALID_CASH_SESSION_UUID",
        }
      );

    return invokeRuntime(
      "getSessionReport",
      () =>
        sessionRuntime
          .getSessionReport(
            normalizedSessionUuid,
            {
              companyUuid:
                scope.companyUuid,
              activityUuid:
                scope.activityUuid,
            }
          ),
      {
        scope,
        cashSessionUuid:
          normalizedSessionUuid,
      }
    );
  }


  return Object.freeze({
    openSession,
    resolveCurrentSession,
    closeSession,
    getSessionHistory,
    getSessionReport,
  });
}


/**
 * Adapter factory for the existing cashRegisterApi contract.
 *
 * This factory does not import cashRegisterApi directly.
 * Callers inject the actual functions.
 */
export function createCashRegisterSessionRuntime({
  openGlobalCashSession,
  getCurrentCashSession,
  closeGlobalCashSession,
  getCashSessionHistory,
  getZReportDetail,
} = {}) {
  assertFunction(
    openGlobalCashSession,
    "openGlobalCashSession"
  );

  assertFunction(
    getCurrentCashSession,
    "getCurrentCashSession"
  );

  assertFunction(
    closeGlobalCashSession,
    "closeGlobalCashSession"
  );

  assertFunction(
    getCashSessionHistory,
    "getCashSessionHistory"
  );

  assertFunction(
    getZReportDetail,
    "getZReportDetail"
  );


  return Object.freeze({
    openSession(
      payload,
      options
    ) {
      return openGlobalCashSession(
        payload,
        options
      );
    },


    resolveCurrentSession({
      companyUuid,
      activityUuid,
    }) {
      return getCurrentCashSession({
        companyUuid,
        activityUuid,
      });
    },


    closeSession(
      cashSessionUuid,
      payload,
      {
        companyUuid,
        activityUuid,
      }
    ) {
      return closeGlobalCashSession(
        cashSessionUuid,
        payload,
        {
          companyUuid,
          activityUuid,
        }
      );
    },


    getSessionHistory({
      companyUuid,
      activityUuid,
    }) {
      return getCashSessionHistory({
        companyUuid,
        activityUuid,
      });
    },


    getSessionReport(
      cashSessionUuid,
      {
        companyUuid,
        activityUuid,
      }
    ) {
      return getZReportDetail(
        cashSessionUuid,
        {
          companyUuid,
          activityUuid,
        }
      );
    },
  });
}


function assertSessionRuntime(
  runtime
) {
  if (
    runtime === null
    || typeof runtime !== "object"
  ) {
    throw new POSActivitySessionBridgeError(
      "sessionRuntime must be an object.",
      {
        code:
          "INVALID_SESSION_RUNTIME",
        stage:
          "initialization",
      }
    );
  }

  for (
    const methodName
    of [
      "openSession",
      "resolveCurrentSession",
      "closeSession",
      "getSessionHistory",
      "getSessionReport",
    ]
  ) {
    if (
      typeof runtime[methodName]
        !== "function"
    ) {
      throw new POSActivitySessionBridgeError(
        `sessionRuntime must expose ${methodName}().`,
        {
          code:
            "INVALID_SESSION_RUNTIME",
          stage:
            "initialization",
          details: {
            methodName,
          },
        }
      );
    }
  }
}


function assertFunction(
  value,
  name
) {
  if (
    typeof value !== "function"
  ) {
    throw new POSActivitySessionBridgeError(
      `${name} must be a function.`,
      {
        code:
          "INVALID_CASH_REGISTER_API",
        stage:
          "initialization",
        details: {
          name,
        },
      }
    );
  }
}


function normalizeSessionScope(
  context,
  operation
) {
  if (!isPlainObject(context)) {
    throw new POSActivitySessionBridgeError(
      "Session context must be a plain object.",
      {
        code:
          "INVALID_SESSION_CONTEXT",
        operation,
        stage:
          "sessionScope",
      }
    );
  }

  const companyUuid =
    normalizeRequiredString(
      context.companyUuid
        ?? context.company_uuid,
      "companyUuid",
      {
        operation,
        code:
          "INVALID_SESSION_CONTEXT",
      }
    );

  const activityUuid =
    normalizeRequiredString(
      context.activityUuid
        ?? context.activity_uuid,
      "activityUuid",
      {
        operation,
        code:
          "INVALID_SESSION_CONTEXT",
      }
    );

  return Object.freeze({
    companyUuid,
    activityUuid,
  });
}


function normalizePayload(
  payload,
  operation
) {
  if (!isPlainObject(payload)) {
    throw new POSActivitySessionBridgeError(
      "Session payload must be a plain object.",
      {
        code:
          "INVALID_SESSION_PAYLOAD",
        operation,
        stage:
          "payload",
      }
    );
  }

  return Object.freeze({
    ...payload,
  });
}


function normalizeRequiredString(
  value,
  fieldName,
  {
    operation = null,
    code =
      "INVALID_SESSION_CONTEXT",
  } = {}
) {
  if (
    typeof value !== "string"
    || value.trim() === ""
  ) {
    throw new POSActivitySessionBridgeError(
      `${fieldName} must be a non-empty string.`,
      {
        code,
        operation,
        stage:
          "validation",
        details: {
          fieldName,
        },
      }
    );
  }

  return value.trim();
}


async function invokeRuntime(
  operation,
  callback,
  details = {}
) {
  try {
    return await Promise.resolve(
      callback()
    );
  } catch (error) {
    throw new POSActivitySessionBridgeError(
      `POSActivity session operation "${operation}" failed.`,
      {
        code:
          "SESSION_RUNTIME_OPERATION_FAILED",
        operation,
        stage:
          "runtime",
        details,
        cause: error,
      }
    );
  }
}


function isPlainObject(
  value
) {
  if (
    value === null
    || typeof value !== "object"
    || Array.isArray(value)
  ) {
    return false;
  }

  const prototype =
    Object.getPrototypeOf(value);

  return (
    prototype === Object.prototype
    || prototype === null
  );
}
